import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';

const AssignSopToUser = () => {
    const [users, setUsers] = useState([]);
    const [sops, setSops] = useState([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedSops, setSelectedSops] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, []);

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };
            const baseUrl = 'https://lbbackend.codingindia.co.in';

            // Fetch simple user list
            const userRes = await axios.get(`${baseUrl}/SOP/users/simple/`, config);
            setUsers(userRes.data);

            // Fetch all SOPs (using management API to get everything)
            const sopRes = await axios.get(`${baseUrl}/SOP/sops/management/`, config);
            setSops(sopRes.data);

            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load users or SOPs.");
            setLoading(false);
        }
    };

    const handleUserChange = (e) => {
        const userId = e.target.value;
        setSelectedUser(userId);

        if (!userId) {
            setSelectedSops([]);
            return;
        }

        // Find SOPs already assigned to this user
        // The management API returns SOPs with a 'users' array containing IDs
        const userSops = sops.filter(sop => sop.users.includes(parseInt(userId))).map(sop => sop.id);
        setSelectedSops(userSops);
    };

    const handleSopToggle = (sopId) => {
        setSelectedSops(prev => {
            if (prev.includes(sopId)) {
                return prev.filter(id => id !== sopId);
            } else {
                return [...prev, sopId];
            }
        });
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedSops(sops.map(sop => sop.id));
        } else {
            setSelectedSops([]);
        }
    };

    const handleSubmit = async () => {
        if (!selectedUser) {
            toast.warning("Please select a user first.");
            return;
        }

        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const baseUrl = 'https://lbbackend.codingindia.co.in';

            await axios.post(
                `${baseUrl}/SOP/sops/assign-users/`,
                { user_id: selectedUser, sop_ids: selectedSops },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            toast.success("SOPs assigned successfully!");
            // Refresh data to ensure state is consistent (though mainly for SOP list if it changed)
            fetchData();

            // Re-select user to update UI state - actually simpler to just keep current state
            // and assume success means backend is updated.
        } catch (error) {
            console.error("Error saving assignments:", error);
            toast.error("Failed to save assignments.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <div className="text-center mt-10">Loading...</div>;
    }

    return (
        <div className="container mx-auto p-4">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Assign SOPs to User</h1>
                <Link to="/sopmanagement" className="text-blue-500 hover:text-blue-700">
                    &larr; Back to SOP Management
                </Link>
            </div>

            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-bold mb-2">Select User</label>
                    <select
                        className="shadow border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        value={selectedUser}
                        onChange={handleUserChange}
                    >
                        <option value="">-- Select a User --</option>
                        {users.map(user => (
                            <option key={user.id} value={user.id}>{user.name} ({user.username})</option>
                        ))}
                    </select>
                </div>

                {selectedUser && (
                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <h2 className="text-lg font-semibold">Assign SOPs</h2>
                            <div>
                                <input
                                    type="checkbox"
                                    id="selectAll"
                                    onChange={handleSelectAll}
                                    checked={selectedSops.length === sops.length && sops.length > 0}
                                    className="mr-2"
                                />
                                <label htmlFor="selectAll" className="font-medium">Select All</label>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto border p-4 rounded">
                            {sops.map(sop => (
                                <div key={sop.id} className="flex items-start p-2 hover:bg-gray-50 rounded border-b">
                                    <input
                                        type="checkbox"
                                        id={`sop-${sop.id}`}
                                        checked={selectedSops.includes(sop.id)}
                                        onChange={() => handleSopToggle(sop.id)}
                                        className="mt-1 mr-2"
                                    />
                                    <label htmlFor={`sop-${sop.id}`} className="cursor-pointer">
                                        <div className="font-medium">{sop.title}</div>
                                        <div className="text-xs text-gray-500">
                                            {sop.designation ? sop.designation.name : 'No Designation'}
                                        </div>
                                    </label>
                                </div>
                            ))}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <button
                                onClick={handleSubmit}
                                disabled={saving}
                                className={`bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {saving ? 'Saving...' : 'Save Assignments'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssignSopToUser;
